import React, { useMemo, useCallback } from 'react';
import propTypes from 'prop-types';
import { UiTable, includesCaseInsensitive } from 'src/ui';
import { Button, MuiDropdown, MuiDropdownItem } from 'src/ui/mui-ui-kit';;
;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink, Link } from 'react-router-dom';
import moment from 'moment';

export function AuditsTable({ loading, audits, onDeleteAudit, onViewNotes, onEditDate, onViewHistory, onFilteredDataChange }) {
  const [tableInstance, setTableInstance] = React.useState(null);
  
  React.useEffect(() => {
    // Extract filters from the table when filtered data changes
    if (tableInstance) {
      const tableState = tableInstance.getResolvedState();
      if (tableState && tableState.filtered) {
        const filters = {};
        tableState.filtered.forEach(filter => {
          if (filter.value) {
            filters[filter.id] = filter.value;
          }
        });
        onFilteredDataChange && onFilteredDataChange(filters);
      }
    }
  }, [audits, tableInstance, onFilteredDataChange]);
  function resolveData(data) {
    return data.map(audit => {
      const newAudit = {
        ...audit,
        statusDisplay: audit.status === 'completed' ? 'Completed' : 'Pending',
        nextAuditDateDisplay: audit.next_audit_date ? moment(audit.next_audit_date).format('MM/DD/YYYY') : '',
        lastAuditDateDisplay: audit.last_audit_date ? moment(audit.last_audit_date).format('MM/DD/YYYY') : '',
      };

      return newAudit;
    });
  }

  const onDelete = useCallback(audit => {
    return () => onDeleteAudit(audit);
  }, []);

  const onNotesClick = useCallback(audit => {
    return () => onViewNotes(audit);
  }, []);

  const onDateEdit = useCallback((audit, field) => {
    return () => onEditDate(audit, field);
  }, []);

  const onHistoryClick = useCallback(audit => {
    return () => onViewHistory(audit);
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Account Name',
        accessor: 'account_name',
        filterMethod: includesCaseInsensitive,
        width: 200,
        Cell: row => (
          <Link className="edit-link" to={`/audits/edit/${row.original.id}`}>
            {row.value}
          </Link>
        ),
      },
      {
        Header: 'Location Address',
        accessor: 'location_address',
        filterMethod: includesCaseInsensitive,
        width: 250,
        Cell: row => (
          <Link className="edit-link" to={`/audits/edit/${row.original.id}`}>
            {row.value}
          </Link>
        ),
      },
      {
        Header: 'Location License',
        accessor: 'location_license',
        filterMethod: includesCaseInsensitive,
        width: 150,
        Cell: row => <div className="has-text-centered">{row.value}</div>,
      },
      {
        Header: 'Next Audit Date',
        accessor: 'nextAuditDateDisplay',
        filterMethod: includesCaseInsensitive,
        width: 140,
        Cell: row => (
          <div className="has-text-centered">
            <a
              className="edit-link"
              onClick={onDateEdit(row.original, 'next_audit_date')}
              style={{ cursor: 'pointer' }}
            >
              {row.value}
            </a>
          </div>
        ),
      },
      {
        Header: 'Last Audit Date',
        accessor: 'lastAuditDateDisplay',
        filterMethod: includesCaseInsensitive,
        width: 140,
        Cell: row => (
          <div className="has-text-centered">
            <a
              className="edit-link"
              onClick={onDateEdit(row.original, 'last_audit_date')}
              style={{ cursor: 'pointer' }}
            >
              {row.value}
            </a>
          </div>
        ),
      },
      {
        Header: 'Status',
        accessor: 'statusDisplay',
        filterMethod: includesCaseInsensitive,
        width: 120,
        Cell: row => (
          <div className="has-text-centered">
            <a
              className="edit-link"
              onClick={onHistoryClick(row.original)}
              style={{ cursor: 'pointer' }}
            >
              <span className={`tag ${row.original.status === 'completed' ? 'is-success' : 'is-warning'}`}>
                {row.value}
              </span>
            </a>
          </div>
        ),
      },
      {
        Header: 'Notes',
        accessor: 'id',
        sortable: false,
        filterable: false,
        width: 100,
        resizable: false,
        Cell: row => (
          <a
            className="edit-link"
            onClick={onNotesClick(row.original)}
            style={{ cursor: 'pointer' }}
          >
            Notes
          </a>
        ),
      },
      {
        Header: '',
        accessor: 'id',
        sortable: false,
        filterable: false,
        width: 62,
        resizable: false,
        Cell: row => (
          <MuiDropdown
            isAlign="right"
            renderTrigger={
              <Button isColor="white">
                <span className="icon">
                  <FontAwesomeIcon
                    icon={['fal', 'ellipsis-v']}
                    className="has-text-grey"
                    size="lg"
                  />
                </span>
              </Button>
            }
          >
              <MuiDropdownItem tag={NavLink} to={`/audits/edit/${row.value}`}>
                Edit
              </MuiDropdownItem>
              <MuiDropdownItem tag="a" onClick={onDelete(row.original)}>
                Delete
              </MuiDropdownItem>
          </MuiDropdown>
        ),
      },
    ],
    [onDelete, onNotesClick, onDateEdit]
  );

  return (
    <UiTable
      useTablePagination
      className="audits-table"
      columns={tableColumns}
      loading={loading}
      data={audits}
      title="Audit"
      emptyTo="/audits/add"
      filterable
      // eslint-disable-next-line
      resolveData={resolveData}
      getTheadThProps={(_state, _rowInfo, column) => ({
        className: ['account_name', 'location_address'].includes(column.id)
          ? 'has-text-left'
          : '',
      })}
      hideNext
      hidePrev
      hideNoDataComponent
      onReactTableInstance={setTableInstance}
    />
  );
}

AuditsTable.propTypes = {
  loading: propTypes.bool.isRequired,
  audits: propTypes.arrayOf(propTypes.object).isRequired,
  onDeleteAudit: propTypes.func.isRequired,
  onViewNotes: propTypes.func.isRequired,
  onEditDate: propTypes.func.isRequired,
  onViewHistory: propTypes.func.isRequired,
};